import { ValueSetterParams } from '@ag-grid-community/core';
import dayjs from 'dayjs';
import { isString, set } from 'lodash-es';

import { MessagesConstants } from '@shared/constants/messages.constants';
import { InvoiceStatus, PaymentStatus } from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';
import { removeSymbolsFromString } from '@shared/utils/formatter.utils';
import { Utils } from '@shared/utils/utils';

export function agCostValueSetter(
  params: ValueSetterParams,
  overlayService: OverlayService,
  validationMessage = MessagesConstants.ONLY_NUMERIC_VALUES_ARE_SUPPORTED
): boolean {
  if (!params.newValue) {
    set(params.data, params.column.getColDef().field || '', 0);

    return true;
  }

  //// Decimal and commas optional, can be changed to be more strict at future
  const currencyRegex = /(?=.*?\d)^[€£¥₹$¢₽₩₦]?(([1-9]\d{0,2}(,\d{3})*)|\d+)?(\.\d{1,2})?$/;
  const isNegWithSlash = String(params.newValue).startsWith('-');
  const isNegWithParnth = String(params.newValue).startsWith('(');
  const testVal = isNegWithSlash
    ? String(params.newValue).slice(1)
    : isNegWithParnth
      ? String(params.newValue).slice(2, -1)
      : params.newValue;
  const isCurrency = currencyRegex.test(testVal);
  const numericString = String(params.newValue).replace(/[^\d.-]/g, '');
  let numberValue = parseFloat(numericString);

  if (!isCurrency && params.newValue !== Utils.zeroHyphen) {
    overlayService.error(validationMessage);
  }

  numberValue = isNegWithParnth ? -numberValue : numberValue;
  const setVal =
    params.newValue === Utils.zeroHyphen ? 0 : isCurrency ? `${numberValue}` : params.oldValue;

  set(params.data, params.column.getColDef().field || '', setVal);

  return true;
}

export function agFormatToFullDateValueSetter(params: ValueSetterParams): boolean {
  const date = dayjs(params.newValue);
  const isDateValid = isNewValDate(String(params.newValue));

  if (!params.newValue || params.newValue === Utils.zeroHyphen) {
    set(params.data, params.column.getColId(), null);

    return true;
  }

  if (isDateValid) {
    set(params.data, params.column.getColId(), date.format('YYYY-MM-DD'));

    return true;
  }

  return false;
}

export function agFormatToMonthDateValueSetter(params: ValueSetterParams): boolean {
  const date = dayjs(params.newValue);
  const isDateValid = isNewValDate(String(params.newValue));

  if (!params.newValue || params.newValue === Utils.zeroHyphen) {
    set(params.data, params.column.getColId(), null);

    return true;
  }

  if (isDateValid) {
    set(params.data, params.column.getColId(), date.format('YYYY-MM'));

    return true;
  }

  return false;
}

export function agFormatToNumberValueSetter(
  params: ValueSetterParams,
  symbolsToRemove: string[],
  field: string
): boolean {
  const newValue = isString(params.newValue)
    ? removeSymbolsFromString(params.newValue, symbolsToRemove)
    : params.newValue;

  set(params.data, field, Number(newValue));

  return true;
}

export function agInvoiceStatusValueSetter(params: ValueSetterParams): boolean {
  const mapStringToInvoiceStatus = new Map<string, InvoiceStatus>([
    ['Approved', InvoiceStatus.STATUS_APPROVED],
    ['Declined', InvoiceStatus.STATUS_DECLINED],
    ['Pending Approval', InvoiceStatus.STATUS_PENDING_APPROVAL],
    ['Pending In Queue', InvoiceStatus.STATUS_IN_QUEUE],
    ['Pending Review', InvoiceStatus.STATUS_PENDING_REVIEW],
  ]);
  const mapInvoiceStatus = new Map<InvoiceStatus, string>([
    [InvoiceStatus.STATUS_APPROVED, 'Approved'],
    [InvoiceStatus.STATUS_DECLINED, 'Declined'],
    [InvoiceStatus.STATUS_IN_QUEUE, 'Pending In Queue'],
    [InvoiceStatus.STATUS_PENDING_APPROVAL, 'Pending Approval'],
    [InvoiceStatus.STATUS_PENDING_REVIEW, 'Pending Review'],
  ]);

  if (mapInvoiceStatus.get(params.newValue)) {
    set(params.data, params.column.getColId(), params.newValue);

    return true;
  }

  if (mapStringToInvoiceStatus.get(params.newValue)) {
    set(params.data, params.column.getColId(), mapStringToInvoiceStatus.get(params.newValue));

    return true;
  }

  if (params.newValue === Utils.zeroHyphen) {
    set(params.data, params.column.getColId(), null);

    return true;
  }

  return false;
}

export function agNumericOnlyValueSetter(
  params: ValueSetterParams,
  overlayService: OverlayService,
  validationMessage = MessagesConstants.ONLY_NUMERIC_VALUES_ARE_SUPPORTED
) {
  const field = params.colDef.field;

  const ANY_NUMERIC_REGEX = /^-?\d+$/gi;

  if (!field) {
    return false;
  }

  if (ANY_NUMERIC_REGEX.test(String(params.newValue)) || params.newValue === null) {
    params.data[field] = params.newValue;

    return true;
  }

  overlayService.error(validationMessage);

  return false;
}

export function agPaymentStatusValueSetter(params: ValueSetterParams): boolean {
  const mapPaymentStatus = new Map<PaymentStatus, string>([
    [PaymentStatus.PAYMENT_STATUS_DECLINED, 'Unpaid'],
    [PaymentStatus.PAYMENT_STATUS_PAID_IN_FULL, 'Paid'],
    [PaymentStatus.PAYMENT_STATUS_PARTIAL_PAYMENT, 'Unpaid'],
    [PaymentStatus.PAYMENT_STATUS_SCHEDULED, 'Unpaid'],
    [PaymentStatus.PAYMENT_STATUS_UNPAID, 'Unpaid'],
  ]);
  const newValAsString = String(params.newValue).toLowerCase();

  if (newValAsString === 'paid') {
    set(params.data, params.column.getColId(), PaymentStatus.PAYMENT_STATUS_PAID_IN_FULL);

    return true;
  }

  if (newValAsString === 'unpaid') {
    set(params.data, params.column.getColId(), PaymentStatus.PAYMENT_STATUS_UNPAID);

    return true;
  }

  if (mapPaymentStatus.get(params.newValue)) {
    set(params.data, params.column.getColId(), params.newValue);

    return true;
  }

  if (params.newValue === Utils.zeroHyphen || !params.newValue) {
    set(params.data, params.column.getColId(), null);

    return true;
  }

  return false;
}

export function isNewValDate(val: string): boolean {
  if ((val.includes('/') || val.includes('-')) && dayjs(val).isValid()) {
    return true;
  } else if (val.includes(' ')) {
    const splittedVal = val.split(' ');

    return Utils.MONTH_NAMES.includes(splittedVal[0]);
  }

  return false;
}
